/**
 * useTranslatedRender.ts
 *
 * Shared view-model hook for the two translated transcript surfaces:
 * `TranscriptionTextTranslated` (regular) and `TranscriptionDiffTranslated`
 * (redline). Both render translation phrases in lead order, anchored to
 * paragraph groups derived from `transcriptElements`. The hook owns:
 *
 *   - phrase indexing (lead, startTs/endTs from source members)
 *   - paragraph grouping with cut/removed visibility per mode
 *   - clip-scope filter (drops phrases outside the kept source range)
 *   - playhead resolution (active phrase by `endTs > currentTime`)
 *   - phrase distribution across groups by lead
 *   - phrase state aggregation from source members (for redline)
 *
 * Each view supplies its own JSX, including state-driven CSS class rules
 * — only the data-shaping is shared.
 */

import { useTranslationStore } from '@src/stores-v2/StoreContext';
import { groupElementsByParagraph } from '@src/utility/transcriptGrouping';
import { useMemo } from 'react';

import {
  buildPhraseMembershipMaps,
  resolvePhraseForElement,
  stableKeysOf,
} from './phraseMembershipMaps';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type {
  TranslationData,
  TranslationPhrase,
} from '@src/types.ts/translation';
import type { TranscriptParagraphGroup } from '@src/utility/transcriptGrouping';

export type TranslatedPhraseEffectiveState =
  | 'kept'
  | 'muted'
  | 'cut'
  | 'removed';

export interface TranslatedSortedPhrase {
  phrase: TranslationPhrase;
  lead: number;
  /**
   * PLAT-10611 (F5) — the stable UUID (`source_uuid ?? id`) of the LEAD ELEMENT
   * (the element whose `initial_index === lead` — the value stamped on
   * `data-initial-index`). Computed ONCE off that same element so the translated
   * span's `data-source-uuid` stamp AND the cross-language link resolver read the
   * IDENTICAL value (F5-DESIGN-MECHANICS §1-note / §7-risk-6: it is NOT
   * `originalElementIds[0]`, which can be a different element). Absent when the
   * lead element carries no id (whitespace / un-migrated) — readers fall back to
   * the integer `lead`.
   */
  leadUuid?: string;
  startTs: number | null;
  endTs: number | null;
  effectiveState: TranslatedPhraseEffectiveState;
}

export type GroupWithSpeakerInfo = TranscriptParagraphGroup & {
  isNewSpeaker: boolean;
};

export interface UseTranslatedRenderOptions {
  /**
   * When true, cut/removed source elements remain in the paragraph spine
   * and `groupIsAllDeleted` is computed (used by the redline view to
   * strike speaker headers). When false, cut/removed are filtered out
   * before grouping (regular view).
   */
  includeOutOfScope: boolean;
  /**
   * PLAT-10266 (Card 7, §B) — OPT-IN. When true, `phrasesByGroup` runs a
   * "pass 3" that re-anchors a TRULY-VANISHED-but-active orphan phrase (its
   * covered source token was DELETED — spliced out of the array by a "Replace
   * Term" edit, so NONE of its `originalElementIds` is present anywhere in
   * `transcriptElements`) back into the paragraph group its OLD lead brackets
   * into, so the Paragraph review can paint its (amber) stale row. Default
   * false. Set ONLY by the whole-story review's `AlignedBlocksView` — the
   * shared prose (`TranscriptionTextTranslated`) and diff
   * (`TranscriptionDiffTranslated`) consumers leave it off and stay
   * byte-identical (§C). Never set on the clip path: there an out-of-clip
   * phrase's ids are legitimately absent from the clip-local array and must
   * NOT be resurfaced (AlignedBlocksView gates on `!clipScoped`).
   */
  backfillVanishedSourcePhrases?: boolean;
}

/**
 * The clip-scope descriptor the hook computes while building `inScopePhrases`.
 * The single source of truth for "what source content is in scope" that BOTH
 * the prose translated panes and the Segment/Fragment aligned view read. A
 * phrase / block is in scope when one of its source indices is in
 * `activeIndices` — a pure intersection (PLAT-10209 H-A; the per-paragraph
 * `groupRanges` fallback was retired once the aligned view started deriving a
 * clip-LOCAL element array, so its `activeIndices` already carries the in-clip
 * cut/removed words and no range bridging is needed).
 *
 *   - `activeIndices` — the source `initial_index` values present in the
 *     visible paragraph groups. In the aligned view's clip-scoped mode the hook
 *     is called with `includeOutOfScope: true` over a clip-local array, so this
 *     spans the clip's full content (kept AND in-clip cut/removed); in the
 *     regular prose view (`includeOutOfScope: false`) cut/removed are filtered
 *     out before this set is built.
 *   - `minIdx` / `maxIdx` — the smallest / largest such index. Retained as an
 *     optional `[min, max]` range a consumer may still apply (see
 *     `AlignedBlocksScope`); the empty-scope case is detected via
 *     `activeIndices.size === 0`, not a sentinel.
 */
export interface TranslatedInScope {
  activeIndices: ReadonlySet<number>;
  /**
   * PLAT-10611 — the stable element UUIDs (`source_uuid ?? id`) of the in-scope
   * source elements, parallel to `activeIndices`. Lets the clip-scope
   * intersection match a phrase by identity (`originalElementIds`) FIRST and
   * fall back to the positional `initial_index` — drift-correct where a clip's
   * indices have shifted from story space. Empty when no in-scope element
   * carries an id (legacy blobs / whitespace).
   */
  activeUuids: ReadonlySet<string>;
  minIdx: number;
  maxIdx: number;
}

export interface UseTranslatedRenderResult {
  translation: TranslationData | null;
  groups: GroupWithSpeakerInfo[];
  /**
   * PLAT-11162 — the exact array `groups`' element indices are positions
   * into (whitespace dropped; cut/removed dropped unless redline). Anything
   * that stamps or resolves those positions must run against THIS array.
   */
  filteredElements: TranscriptElement[];
  phrasesByGroup: TranslatedSortedPhrase[][];
  currentLead: number | null;
  /**
   * PLAT-10611 (F5) — the active phrase's lead UUID (`source_uuid ?? id` of the
   * element at `currentLead`), parallel to `currentLead`. The aligned view's
   * active-phrase autoscroll queries `[data-source-uuid]` FIRST, falling back to
   * `[data-initial-index={currentLead}]` — drift-correct on a reordered clip
   * where the integer would scroll to the wrong span. `null` when no active
   * phrase or its lead element carries no id.
   */
  currentLeadUuid: string | null;
  groupIsAllDeleted: boolean[];
  /**
   * The lead-sorted phrases that survive the clip-scope filter (the same list
   * `phrasesByGroup` is distributed from). Exposed so the aligned view reuses
   * the prose path's scoped result instead of recomputing its own scope from
   * the rendered original elements (which, in CLIP mode, still carry out-of-
   * clip removed words with story-absolute indices — PLAT-10264).
   */
  inScopePhrases: TranslatedSortedPhrase[];
  /**
   * The clip-scope descriptor (active source-index set + `[minIdx, maxIdx]`
   * range) used to compute `inScopePhrases`. The aligned view applies the same
   * membership test to its blocks so it keeps EXACTLY the blocks the prose
   * path keeps. See {@link TranslatedInScope}.
   */
  inScope: TranslatedInScope;
}

const STATE_RANK: Record<TranslatedPhraseEffectiveState, number> = {
  kept: 0,
  muted: 1,
  cut: 2,
  removed: 3,
};

function worseState(
  a: TranslatedPhraseEffectiveState,
  b: TranslatedPhraseEffectiveState,
): TranslatedPhraseEffectiveState {
  return STATE_RANK[a] >= STATE_RANK[b] ? a : b;
}

function betterState(
  a: TranslatedPhraseEffectiveState,
  b: TranslatedPhraseEffectiveState,
): TranslatedPhraseEffectiveState {
  return STATE_RANK[a] <= STATE_RANK[b] ? a : b;
}

/**
 * A phrase's effective redline state, aggregated MOVE-aware. `bestByKey` holds
 * the BEST (least-struck) state seen per source word — keyed by `source_uuid`
 * when present, else by element `id` / `initial_index`. The phrase's state is
 * the WORST across those per-word bests: a word that is kept in ANY occurrence
 * counts as kept (so a montage MOVE — the same source word struck at its old
 * spot and kept at its re-add — reads kept), while a genuine partial cut
 * (a DISTINCT source word cut) still reads struck. Keying by `source_uuid`
 * is what collapses a move's two occurrences; id-less legacy data keys by the
 * distinct `id`s, so both occurrences count and the old worst-wins behavior is
 * preserved. Empty map ⇒ `'kept'`.
 */
function aggregateEffectiveState(
  bestByKey: Map<string, TranslatedPhraseEffectiveState>,
): TranslatedPhraseEffectiveState {
  let result: TranslatedPhraseEffectiveState = 'kept';
  let seen = false;
  for (const s of bestByKey.values()) {
    result = seen ? worseState(result, s) : s;
    seen = true;
  }
  return result;
}

function elStateToEffective(
  state: TranscriptElement['state'] | undefined,
): TranslatedPhraseEffectiveState {
  if (state === 'removed') return 'removed';
  if (state === 'cut') return 'cut';
  if (state === 'muted') return 'muted';
  return 'kept';
}

/**
 * Pure helper used by `useTranslatedRender` (paragraph view) AND any
 * caller that just needs the lead-sorted phrase list with start/end
 * timestamps — e.g. PLAT-10066's `findTranslatedOverlayRanges` driver in
 * `Transcript.tsx`. Kept here so the lead-derivation + state aggregation
 * + endTs backfill are not duplicated across consumers.
 *
 * `includeState` is needed for the redline view's strike-through styling;
 * for the underline range mapping it defaults to `'kept'` which is
 * harmless.
 */
export function buildTranslatedPhraseTimeInfos(
  translation: TranslationData | null,
  transcriptElements: TranscriptElement[] | undefined,
): TranslatedSortedPhrase[] {
  if (!translation) return [];

  // PLAT-10611 — membership is UUID-first (`source_uuid ?? id`), positional
  // `initial_index` fallback. Only `uuid → phrase` is built (DESIGN §4.6); the
  // matched ELEMENT (never a map) carries timing/order/lead.
  const { phraseByUuid } = buildPhraseMembershipMaps(translation.phrases);

  // ORDER + LEAD come from the LIVE render position: pre-walk `transcriptElements`
  // ONCE, resolving each element to its phrase by identity. The FIRST element (in
  // render order) that resolves to a phrase fixes that phrase's render position
  // (its sort key) and its lead VALUE (that element's `initial_index` — still a
  // real index, so `data-initial-index` / seek / scroll / linked-highlight keep
  // working). TIMING aggregates off the matched element itself (montage clock
  // where present) — the old `elementByInitialIndex` indirection is gone.
  //
  // Only ACTIVE fragments participate (the membership maps exclude
  // `active === false` D10-RETIRED phrases superseded by an override), so a
  // retired original never renders beside its override. cut/removed is the
  // source-element STATE (still active) and is aggregated into `effectiveState`
  // for the redline view.
  type PhraseAccumulator = {
    lead: number;
    // PLAT-10611 (F5) — the lead element's stable UUID, captured from the SAME
    // element that fixes `lead` (so the stamp and the resolver agree —
    // §7-risk-6). `undefined` when that element carries no id.
    leadUuid?: string;
    startTs: number | null;
    endTs: number | null;
    renderPos: number;
    // True once the render anchor (lead / leadUuid / renderPos) sits on a KEPT
    // occurrence. A montage MOVE leaves the phrase's original words struck
    // (`removed`) at their OLD array position and re-adds KEPT copies later, so
    // the FIRST-appearing element is the struck ghost. We re-anchor onto the
    // first kept occurrence so the phrase orders / leads / seeks at the copy that
    // actually plays. A fully-deleted phrase never flips this and keeps its first
    // (struck) anchor. On a non-reordered transcript the first element is already
    // kept, so this is a no-op and the output is unchanged.
    anchoredOnKept: boolean;
    // Best (least-struck) state per source word — see `aggregateEffectiveState`.
    bestStateByKey: Map<string, TranslatedPhraseEffectiveState>;
  };
  const accByPhrase = new Map<TranslationPhrase, PhraseAccumulator>();

  if (transcriptElements) {
    for (let pos = 0; pos < transcriptElements.length; pos++) {
      const el = transcriptElements[pos];
      // Synthetic placeholders (negative index) never join a phrase — parity
      // with the old integer element map, which excluded them.
      const uuidKey = el.source_uuid ?? el.id;
      if (!uuidKey) continue;

      // PLAT-11753 — either key: a translation produced after a "Replace Term"
      // edit records the element's MINTED `id`, not its inherited `source_uuid`.
      let phrase: TranslationPhrase | undefined = resolvePhraseForElement(
        el,
        phraseByUuid,
      );
      if (!phrase) continue;

      const elState = elStateToEffective(el.state);
      const elKept = elState !== 'removed' && elState !== 'cut';

      let acc = accByPhrase.get(phrase);
      if (!acc) {
        // First-appearing member provisionally fixes render position + lead
        // VALUE + lead UUID (the SAME element, so `data-source-uuid` and
        // `data-initial-index` on the translated span always describe one
        // element — F5 §7-risk-6).
        acc = {
          lead: el.initial_index,
          leadUuid: uuidKey,
          startTs: null,
          endTs: null,
          renderPos: pos,
          anchoredOnKept: elKept,
          bestStateByKey: new Map(),
        };
        accByPhrase.set(phrase, acc);
      } else if (!acc.anchoredOnKept && elKept) {
        // Re-anchor onto the FIRST KEPT occurrence: the provisional anchor was a
        // struck moved-from ghost; the kept re-add is where the phrase plays, so
        // it owns render position / lead / lead UUID.
        acc.lead = el.initial_index;
        acc.leadUuid = uuidKey;
        acc.renderPos = pos;
        acc.anchoredOnKept = true;
      }

      // Track the best state per source word (MOVE-aware — see
      // `aggregateEffectiveState`). Key by `source_uuid` so a move's struck
      // ghost and kept re-add of the SAME word collapse to their kept state;
      // fall back to the distinct `id` / index for id-less legacy data (old
      // worst-wins behavior).
      const stateKey = el.source_uuid ?? el.id ?? `idx:${el.initial_index}`;
      const prevBest = acc.bestStateByKey.get(stateKey);
      acc.bestStateByKey.set(
        stateKey,
        prevBest === undefined ? elState : betterState(prevBest, elState),
      );

      // Timing feeds ONLY the active-phrase highlight pick, so aggregate it from
      // KEPT text elements only. A moved-from ghost (`ts` null on the clip path,
      // stale recording `ts` on whole-story) would otherwise pollute the
      // phrase's play-interval and mis-fire the highlight.
      if (el.type === 'text' && elKept) {
        // `ts`/`end_ts` is the clock the active player reports: the packed
        // montage timeline on the clip path, recording on whole-story. Both are
        // already correct for the interval-containment pick below.
        const tsVal = typeof el.ts === 'number' ? el.ts : null;
        const endTsVal = typeof el.end_ts === 'number' ? el.end_ts : null;
        if (tsVal !== null && (acc.startTs === null || tsVal < acc.startTs)) {
          acc.startTs = tsVal;
        }
        if (endTsVal !== null && (acc.endTs === null || endTsVal > acc.endTs)) {
          acc.endTs = endTsVal;
        }
      }
    }
  }

  // INCLUSION stays phrase-driven: iterate `translation.phrases` so NO phrase is
  // dropped (incl. null-timing phrases whose members never rendered — they keep
  // their integer `min(originalIndices)` lead and sort last). Only the ORDER KEY
  // (render position) and the LEAD VALUE (first-rendered member) moved. The
  // per-lead map keeps ONE fragment per lead VALUE, last-wins — matching the
  // override resolver (overrides are appended to `phrases[]`, so the last write
  // wins over any stale twin sharing a lead).
  const byLead = new Map<
    number,
    { info: TranslatedSortedPhrase; renderPos: number }
  >();
  for (const phrase of translation.phrases) {
    if (phrase.originalIndices.length === 0) continue;
    if (phrase.active === false) continue;
    const acc = accByPhrase.get(phrase);
    const lead = acc ? acc.lead : Math.min(...phrase.originalIndices);
    const info: TranslatedSortedPhrase = {
      phrase,
      lead,
      // PLAT-10611 (F5) — carry the lead element's UUID from the render walk. A
      // phrase whose members never rendered (acc undefined) has no on-screen
      // span, so `leadUuid` stays undefined and its readers fall back to `lead`.
      leadUuid: acc ? acc.leadUuid : undefined,
      startTs: acc ? acc.startTs : null,
      endTs: acc ? acc.endTs : null,
      effectiveState: acc
        ? aggregateEffectiveState(acc.bestStateByKey)
        : 'kept',
    };
    byLead.set(lead, {
      info,
      renderPos: acc ? acc.renderPos : Number.POSITIVE_INFINITY,
    });
  }

  const working = [...byLead.values()];
  working.sort((a, b) => {
    // Sort by first render position; phrases with no rendered member
    // (renderPos = Infinity) sort last, ordered among themselves by lead. On a
    // non-reordered transcript (elements ascending) render position agrees with
    // the integer lead, so the output is byte-identical to the old lead sort.
    if (a.renderPos !== b.renderPos) {
      if (a.renderPos === Number.POSITIVE_INFINITY) return 1;
      if (b.renderPos === Number.POSITIVE_INFINITY) return -1;
      return a.renderPos - b.renderPos;
    }
    return a.info.lead - b.info.lead;
  });
  const arr: TranslatedSortedPhrase[] = working.map(w => w.info);

  // Backfill missing endTs with the next phrase's startTs. Phrases
  // attached to a punct-only source index (e.g. ",") have no
  // intrinsic timing but still need an endTs for the
  // `endTs > currentTime` lookup to ever succeed.
  for (let i = 0; i < arr.length; i++) {
    if (arr[i].endTs !== null) continue;
    for (let j = i + 1; j < arr.length; j++) {
      if (arr[j].startTs !== null) {
        arr[i].endTs = arr[j].startTs;
        break;
      }
    }
  }
  return arr;
}

export function useTranslatedRender(
  language: string,
  transcriptElements: TranscriptElement[] | undefined,
  currentTime: number | undefined,
  options: UseTranslatedRenderOptions,
): UseTranslatedRenderResult {
  const translationStore = useTranslationStore();
  const translation = translationStore.getTranslation(language);
  const includeOutOfScope = options.includeOutOfScope;
  // PLAT-10266 (Card 7, §B) — the opt-in vanished-source backfill (see the
  // option doc). Off for every consumer except the whole-story review.
  const backfillVanishedSourcePhrases =
    options.backfillVanishedSourcePhrases === true;

  // Phrases sorted by lead. Each carries its derived time range and
  // (for redline) its aggregated source state.
  const sortedPhrases = useMemo<TranslatedSortedPhrase[]>(
    () => buildTranslatedPhraseTimeInfos(translation, transcriptElements),
    [translation, transcriptElements],
  );

  // Paragraph spine. Regular view drops cut/removed; redline keeps them.
  // The filtered array is exposed on the result (PLAT-11162): the groups'
  // `elements[].index` are positions into IT, so anything that stamps or
  // resolves those positions must run against this exact array.
  const filteredElements = useMemo(() => {
    if (!transcriptElements) return [];
    return transcriptElements.filter(el => {
      if (el.subtype === 'whitespace') return false;
      if (includeOutOfScope) return true;
      return el.state !== 'cut' && el.state !== 'removed';
    });
  }, [transcriptElements, includeOutOfScope]);
  const visibleGroups = useMemo(
    () => groupElementsByParagraph(filteredElements),
    [filteredElements],
  );

  const groups = useMemo<GroupWithSpeakerInfo[]>(() => {
    let prev: string | undefined;
    return visibleGroups.map(g => {
      const isNewSpeaker = g.speakerLabel !== prev;
      prev = g.speakerLabel;
      return { ...g, isNewSpeaker };
    });
  }, [visibleGroups]);

  // Clip-scope descriptor: the source-index set + `[minIdx, maxIdx]` range
  // covered by the visible paragraph groups. The single source of truth for
  // "what's in scope" — both `inScopePhrases` (below) and the Segment/Fragment
  // aligned view (which reads this off the hook's result) apply the SAME
  // membership test against it (PLAT-10264). Extracted from the old
  // `inScopePhrases` memo so the set/range is computed once and shared, not
  // recomputed by each consumer.
  const inScope = useMemo<TranslatedInScope>(() => {
    const activeIndices = new Set<number>();
    const activeUuids = new Set<string>();
    let minIdx = Infinity;
    let maxIdx = -Infinity;
    for (const g of groups) {
      for (const { element } of g.elements) {
        const idx = element.initial_index;
        activeIndices.add(idx);
        // PLAT-10611 — stable-id membership alongside the integer set (the
        // whole-story `[minIdx, maxIdx]` band stays integer; that is F5).
        // PLAT-11753 — under EVERY key a stored blob may have recorded the
        // element under, because `inScopePhrases` has no positional fallback
        // (PLAT-10646): a phrase whose id misses here is dropped outright, not
        // merely mis-anchored. A translation produced after a "Replace Term"
        // edit references the MINTED `id`, so keying this set by `source_uuid`
        // alone silently removed the replaced word's phrase from the translated
        // transcript (PLAT-11682).
        for (const key of stableKeysOf(element)) activeUuids.add(key);
        if (idx < minIdx) minIdx = idx;
        if (idx > maxIdx) maxIdx = idx;
      }
    }
    return { activeIndices, activeUuids, minIdx, maxIdx };
  }, [groups]);

  // Clip-scope: keep a phrase only when one of its source indices intersects
  // the in-scope active set — a pure intersection (PLAT-10209 H-A). Without it,
  // in clip mode every out-of-clip phrase would dump into the first paragraph
  // group. The aligned view (`AlignedBlocksView`) applies the SAME intersection
  // to its blocks (`blockInScope` in `alignedBlocks.ts`); in its clip-scoped
  // mode it feeds the hook a clip-LOCAL element array with `includeOutOfScope:
  // true`, so `activeIndices` already carries the in-clip cut/removed words and
  // the old per-group range fallback is no longer needed.
  const inScopePhrases = useMemo<TranslatedSortedPhrase[]>(() => {
    const { activeIndices, activeUuids } = inScope;
    if (activeIndices.size === 0) return [];
    return sortedPhrases.filter(({ phrase }) => {
      // PLAT-10611 — stable-id membership: a phrase is in scope when one of its
      // element UUIDs is rendered in-scope. Every live content phrase carries
      // `originalElementIds`, so no positional fallback is needed (PLAT-10646).
      if (phrase.originalElementIds) {
        for (const id of phrase.originalElementIds) {
          if (activeUuids.has(id)) return true;
        }
      }
      return false;
    });
  }, [inScope, sortedPhrases]);

  // Active phrase (highlight). Redline mode skips cut/removed — they aren't on
  // the playback path.
  //
  // PLAT-10464: a single, source-AGNOSTIC pick — interval-containment on each
  // phrase's play-interval `[startTs, endTs)`. The play-interval comes from the
  // elements' `ts`/`end_ts`, which is already the clock the active player
  // reports: the packed montage timeline on the clip path, recording on
  // whole-story. So the
  // pick no longer branches on source — it asks "which phrase's interval
  // contains `currentTime`", falling back to the nearest FOLLOWING phrase by
  // start when `currentTime` sits in a gap (a cut-gap between montage segments,
  // or silence on whole-story — mirrors the clip preview's upcoming-word
  // behavior). Phrases with no timing (`startTs`/`endTs` null — cut/removed or
  // punct-only) are skipped, so they never become active.
  //
  // Why interval-containment and not the old "first endTs > currentTime" scan:
  // on the clip path the phrases are lead-ordered but the montage plays its
  // segments OUT of source order, so `endTs` is non-monotonic and the scan
  // returns the wrong phrase. On whole-story the recording intervals ARE
  // monotonic, so interval-containment returns the same phrase the scan did at
  // word starts / gaps / before-first / after-last — the one intended
  // difference being that a punct-only phrase (`startTs === null`) is skipped
  // here, so the highlight lands on the next real word rather than a lone comma.
  const currentPhrase = useMemo<TranslatedSortedPhrase | null>(() => {
    if (currentTime === undefined) return null;
    const isActiveCandidate = (p: TranslatedSortedPhrase): boolean =>
      !(
        includeOutOfScope &&
        (p.effectiveState === 'cut' || p.effectiveState === 'removed')
      );

    let contained: TranslatedSortedPhrase | null = null;
    let containedStart = -Infinity;
    let nextAfter: TranslatedSortedPhrase | null = null;
    let nextAfterStart = Infinity;
    for (const p of inScopePhrases) {
      if (!isActiveCandidate(p)) continue;
      const start = p.startTs;
      const end = p.endTs;
      if (start === null || end === null) continue;
      if (currentTime >= start && currentTime < end) {
        // Innermost / latest-starting containing interval wins if intervals
        // ever abut, so the most-current phrase is chosen deterministically.
        if (start > containedStart) {
          contained = p;
          containedStart = start;
        }
      } else if (start > currentTime && start < nextAfterStart) {
        nextAfter = p;
        nextAfterStart = start;
      }
    }
    return contained ?? nextAfter;
  }, [inScopePhrases, currentTime, includeOutOfScope]);

  // The active phrase's lead source index (integer, unchanged contract) and its
  // stable lead UUID (PLAT-10611 F5). Both derive from the SAME chosen phrase, so
  // the aligned view's active-phrase scroll can query `data-source-uuid` FIRST
  // and fall back to `data-initial-index={currentLead}` (§6). Primitive results,
  // no memo needed.
  const currentLead = currentPhrase ? currentPhrase.lead : null;
  const currentLeadUuid = currentPhrase
    ? (currentPhrase.leadUuid ?? null)
    : null;

  // Distribute in-scope phrases across paragraph groups by MEMBERSHIP: each
  // phrase goes to the group that actually contains one of its source indices.
  // The previous lead-vs-threshold two-pointer assumed groups were ordered by
  // ascending source index, but a montage orders groups by PLAYBACK time, so a
  // later group can start at a lower source index (e.g. a "title sponsor"
  // segment from source idx 228 plays AFTER an "I always tell them" segment
  // from idx 12482). The monotonic walk then over-grabbed one group and emptied
  // the next. A direct index->group map is correct regardless of group order
  // (PLAT-10209). Within a group, `inScopePhrases` order is preserved, which
  // inside a single contiguous clip segment is playback order.
  const phrasesByGroup = useMemo<TranslatedSortedPhrase[][]>(() => {
    const out: TranslatedSortedPhrase[][] = groups.map(() => []);
    if (out.length === 0 || inScopePhrases.length === 0 || !translation) {
      return out;
    }
    // PLAT-10611 — re-key group assignment to element IDENTITY (DESIGN §4.2/§4.6):
    // walk each group's elements in order and resolve each to its phrase by
    // `source_uuid ?? id`, so a drifted clip element still
    // lands its phrase in the group it actually renders in. The FIRST group
    // element that resolves to a phrase fixes that phrase's group — "the first
    // member that belongs to a group, not strictly the lead" (a lead can be a
    // cut/filtered word). Resolved per-live-element on the walk into a
    // `phrase → group` assignment (one entry per phrase); NO `uuid → group` map,
    // which would collide last-wins on a reordered clip that reuses a source
    // element (same `source_uuid`, fresh `id`) across groups.
    const { phraseByUuid } = buildPhraseMembershipMaps(translation.phrases);
    const groupByPhrase = new Map<TranslationPhrase, number>();
    // Assign each phrase to the group of its first KEPT occurrence (pass 1),
    // then backfill any still-unassigned phrase from its removed/cut group
    // (pass 2). A montage MOVE renders the phrase's struck ghost in its OLD group
    // and the kept re-add in its NEW group; keying the group off the first
    // resolving element (any state) then pinned the phrase to the old group and
    // the kept copy's slot rendered nothing. Preferring the kept occurrence
    // places the phrase where it plays (mirroring the render-position anchor in
    // `buildTranslatedPhraseTimeInfos`), while a genuinely-deleted phrase still
    // lands in its struck group so the redline shows it. The regular view's
    // groups carry no removed/cut elements, so pass 1 assigns everything and
    // pass 2 is a no-op there.
    const assignGroups = (keptOnly: boolean): void => {
      groups.forEach((g, gi) => {
        for (const { element } of g.elements) {
          if (
            keptOnly &&
            (element.state === 'removed' || element.state === 'cut')
          ) {
            continue;
          }
          // PLAT-11753 — either key; see `resolvePhraseForElement`.
          const phrase = resolvePhraseForElement(element, phraseByUuid);
          if (phrase && !groupByPhrase.has(phrase)) {
            groupByPhrase.set(phrase, gi);
          }
        }
      });
    };
    assignGroups(true);
    assignGroups(false);
    for (const p of inScopePhrases) {
      const gi = groupByPhrase.get(p.phrase);
      if (gi !== undefined) out[gi].push(p);
    }

    // PLAT-10266 (Card 7, §B) — PASS 3, opt-in vanished-source backfill. A
    // still-active phrase whose covered source token was DELETED (spliced out of
    // the array by a "Replace Term" edit) was dropped at the clip-scope Gate 1
    // (its dead id is not in `activeUuids`), so it never reached `inScopePhrases`
    // above — the Paragraph review then had no row to paint its stale amber on.
    // Pull the orphan from `sortedPhrases` (the FULL list; Gate 1 already removed
    // it from `inScopePhrases`), keep only truly-vanished-active orphans, and
    // re-anchor each by its OLD lead into the paragraph group whose surviving
    // index range brackets it. OFF for prose/diff (byte-identical) and the clip
    // path (out-of-clip ids are legitimately absent — never resurfaced).
    if (backfillVanishedSourcePhrases) {
      // The stable-id SET present anywhere in the FULL array (content AND
      // struck cut/removed elements — the includeOutOfScope:false filter only
      // touches the paragraph spine, not this raw list). A redline-CUT phrase
      // keeps its id here (state 'cut'/'removed', still present), so it is NOT
      // "vanished" and is never backfilled; only a spliced-out DELETE is.
      const presentSourceKeys = new Set<string>();
      if (transcriptElements) {
        for (const el of transcriptElements) {
          const key = el.source_uuid ?? el.id;
          if (key != null) presentSourceKeys.add(key);
        }
      }
      const inScopeSet = new Set(inScopePhrases.map(p => p.phrase));
      // The group a vanished orphan's OLD lead brackets into: the last group
      // whose smallest surviving CONTENT index is ≤ the lead (ascending groups —
      // whole-story only, which the flag implies). Falls back to group 0 for a
      // lead that sits before every surviving group.
      const groupStart = (gi: number): number => {
        let min = Infinity;
        for (const { element } of groups[gi].elements) {
          if (element.initial_index >= 0 && element.initial_index < min) {
            min = element.initial_index;
          }
        }
        return min;
      };
      for (const sp of sortedPhrases) {
        const phrase = sp.phrase;
        // Already placed (in scope or assigned a group) — never double-render.
        if (inScopeSet.has(phrase) || groupByPhrase.has(phrase)) continue;
        // `sortedPhrases` already excludes retired / empty phrases; require a
        // stable-id anchor (every live content phrase carries one — PLAT-10646).
        const ids = phrase.originalElementIds;
        if (!ids || ids.length === 0) continue;
        // TRULY VANISHED: none of the covered ids present anywhere in the array
        // (a DELETE, not a cut). This is the discriminator that keeps a normal
        // phrase (≥1 id present) and a redline-cut phrase (id present) out.
        let vanished = true;
        for (const id of ids) {
          if (presentSourceKeys.has(id)) {
            vanished = false;
            break;
          }
        }
        if (!vanished) continue;
        let targetGi = 0;
        for (let gi = 0; gi < groups.length; gi++) {
          if (groupStart(gi) <= sp.lead) targetGi = gi;
        }
        // PLAT-10266 (2026-07-28) — insert at the orphan's LEAD position, do not
        // append. `buildParagraphBlocks` joins each group's phrases as-is ("no
        // re-sort", alignedBlocks.ts:354), so a pushed orphan rendered LAST in the
        // paragraph: replacing a word left its own slot empty and stranded the
        // translated word at the end of the row (Denys, "figma" → "figmas"). Safe to
        // order by `.lead` here because the orphan and its siblings both come from
        // `sortedPhrases`, so they share one key space, and this pass is OFF for the
        // clip path — where group order is montage order, not ascending lead.
        const insertAt = out[targetGi].findIndex(
          existing => existing.lead > sp.lead,
        );
        if (insertAt === -1) out[targetGi].push(sp);
        else out[targetGi].splice(insertAt, 0, sp);
      }
    }
    return out;
  }, [
    groups,
    inScopePhrases,
    translation,
    backfillVanishedSourcePhrases,
    sortedPhrases,
    transcriptElements,
  ]);

  // Redline-only: a paragraph is "all deleted" when every text element
  // in it is cut or removed. Used to strike the speaker header.
  const groupIsAllDeleted = useMemo<boolean[]>(() => {
    if (!includeOutOfScope) return groups.map(() => false);
    return groups.map(g => {
      let sawText = false;
      for (const { element } of g.elements) {
        if (element.type !== 'text') continue;
        sawText = true;
        if (element.state !== 'cut' && element.state !== 'removed') {
          return false;
        }
      }
      return sawText;
    });
  }, [groups, includeOutOfScope]);

  return {
    translation,
    groups,
    filteredElements,
    phrasesByGroup,
    currentLead,
    currentLeadUuid,
    groupIsAllDeleted,
    inScopePhrases,
    inScope,
  };
}
